import React, { useState, useEffect, useCallback } from "react";
import { Shimmer } from "react-shimmer";
import QuestionReportSystemRow from "./QuestionReportSystemRow";
import { getAllReportedQuestions, getExamTestType, getExamTestTypeSubCategory } from "../../../api/testSeries/QuestionReportSystem";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

// Constants for report status to ensure consistency
const REPORT_STATUS = {
    RESOLVED: "RESOLVED",
    UNRESOLVED: "UNRESOLVED",
    ALL: "ALL"
};

// Item count options for pagination
const ITEM_COUNT_OPTIONS = [25, 50, 75, 100];

const QuestionReportSystem = (props) => {
    const [offset, setOffset] = useState(0);
    const [showShimmer] = useState(false);
    const [itemCount, setItemCount] = useState(25);

    const [isLast, setIsLast] = useState(false);
    const [questionReportSystemData, setQuestionReportSystemData] = useState([]);
    const [questionReportFilterData, setQuestionReportFilterData] = useState([]);
    const [showLoader, setShowLoader] = useState(true);
    const [currentFilter, setCurrentFilter] = useState(REPORT_STATUS.ALL);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Pagination information
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    // Filter modal state
    const [showFilterModal, setShowFilterModal] = useState(false);

    // Additional filter states
    const [statusFilter, setStatusFilter] = useState(REPORT_STATUS.ALL);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");

    // Mock data for subjects and categories (to be replaced with API data)
    const [subjects, setSubjects] = useState([
        { id: "1", title: "Mathematics" },
        { id: "2", title: "Physics" },
        { id: "3", title: "Chemistry" },
        { id: "4", title: "Biology" }
    ]);

    const [categories, setCategories] = useState([
        { id: "1", title: "Category 1" },
        { id: "2", title: "Category 2" },
        { id: "3", title: "Category 3" },
        { id: "4", title: "Category 4" }
    ]);

    // Function to fetch subjects (skeleton, to be implemented with API)
    const fetchSubjects = async () => {
        try {
            const res = await getExamTestType();
            setSubjects(res?.data?.rows);
        } catch (error) {
            console.error("Error fetching subjects:", error);
        }
    };

    // Function to fetch categories (skeleton, to be implemented with API)
    const fetchCategories = async () => {
        try {
            const res = await getExamTestTypeSubCategory(selectedSubject);
            setCategories(res?.data?.subcategories);
            // setCategories(response.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    // Load subjects and categories when component mounts
    useEffect(() => {
        fetchSubjects();

    }, []);

    useEffect(() => {
        if (!selectedSubject || selectedSubject?.length === 0 || selectedSubject === "") {
            return
        } else {

            fetchCategories();
        }
    }, [selectedSubject])

    // Function to standardize and sort data by report status (UNRESOLVED first, then RESOLVED)
    const reorderByReportStatus = useCallback((data) => {
        if (!Array.isArray(data)) return [];

        return data.sort((a, b) => {
            const statusA = (a.reportStatus || "").toUpperCase();
            const statusB = (b.reportStatus || "").toUpperCase();

            if (statusA === REPORT_STATUS.UNRESOLVED && statusB === REPORT_STATUS.RESOLVED) {
                return -1;
            } else if (statusA === REPORT_STATUS.RESOLVED && statusB === REPORT_STATUS.UNRESOLVED) {
                return 1;
            }
            return 0;
        });
    }, []);

    // Apply filter based on the selected option
    const handleFilterChange = (event) => {
        const filterValue = event.target.value;
        setCurrentFilter(filterValue);
        setOffset(0); // Reset to first page when filter changes
        setRefreshTrigger(prev => prev + 1); // Trigger refresh with new filter
    };

    // Handle item count change
    const handleItemCountChange = (event) => {
        const newItemCount = parseInt(event.target.value);
        setItemCount(newItemCount);
        setOffset(0); // Reset to first page when item count changes
        setRefreshTrigger(prev => prev + 1); // Trigger refresh with new item count
    };

    // Function to open filter modal
    const openFilterModal = () => {
        // Initialize filter options with current values
        setStatusFilter(currentFilter);
        setShowFilterModal(true);
    };

    // Function to apply filters
    const applyFilters = () => {
        setCurrentFilter(statusFilter);
        setOffset(0); // Reset to first page when filters change
        setShowFilterModal(false);
        setRefreshTrigger(prev => prev + 1); // Trigger refresh with new filters
    };

    // Function to clear filters
    const clearFilters = () => {
        setStatusFilter(REPORT_STATUS.ALL);
        setStartDate(null);
        setEndDate(null);
        setSelectedSubject("");
        setSelectedCategory("");
    };

    // Refresh function that can be called to reload data
    const refreshData = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    // Function to fetch question report data with the current filter
    const fetchQuestionReportData = useCallback(async () => {
        setShowLoader(true);
        try {
            // Pass the currentFilter as status parameter (empty string for ALL)
            const filterParam = currentFilter === REPORT_STATUS.ALL ? '' : currentFilter;

            // Create filter params object for API
            const filterParams = {
                status: filterParam,
                startDate: startDate ? startDate.toISOString() : null,
                endDate: endDate ? endDate.toISOString() : null,
                subject: selectedSubject || null,
                category: selectedCategory || null
            };

            console.log("Filter params:", filterParams);

            // For now, we'll continue to use the existing API while it's being updated
            const response = await getAllReportedQuestions(
                offset,
                itemCount,
                filterParam,
                startDate,
                endDate,
                selectedSubject, // examTestTypeId
                selectedCategory // examTestTypeSubcategoryId
            );

            if (response && response.status) {
                const processedData = reorderByReportStatus(response?.data?.rows || []);
                setQuestionReportSystemData(processedData);
                setQuestionReportFilterData(processedData);
                setIsLast(response.data.last);

                // Set pagination information
                setCurrentPage(response.data.currentPage || 0);
                setTotalPages(response.data.totalPages || 0);
                setTotalItems(response.data.totalItems || 0);
            } else {
                console.error("Error fetching question reports:", response?.message || "Unknown error");
            }
        } catch (error) {
            console.error("Exception in fetching question reports:", error);
        } finally {
            setShowLoader(false);
        }
    }, [offset, currentFilter, itemCount, reorderByReportStatus, startDate, endDate, selectedSubject, selectedCategory]);

    useEffect(() => {
        fetchQuestionReportData();
    }, [fetchQuestionReportData, offset, refreshTrigger]);

    // Handle row update - this will trigger when a status is changed
    const handleRowUpdate = useCallback((rowId, newStatus) => {
        if (rowId && newStatus) {
            // If we're filtered and the new status doesn't match our filter, refresh data
            if (currentFilter !== REPORT_STATUS.ALL && newStatus !== currentFilter) {
                refreshData();
                return;
            }

            // Otherwise, update the status in our local data and resort
            setQuestionReportSystemData(prevData => {
                const updatedData = prevData.map(row => {
                    if (row.id === rowId) {
                        return { ...row, reportStatus: newStatus };
                    }
                    return row;
                });
                return reorderByReportStatus(updatedData);
            });

            setQuestionReportFilterData(prevData => {
                const updatedData = prevData.map(row => {
                    if (row.id === rowId) {
                        return { ...row, reportStatus: newStatus };
                    }
                    return row;
                });
                return reorderByReportStatus(updatedData);
            });
        } else {
            // If no specific row info, just refresh all data
            refreshData();
        }
    }, [currentFilter, refreshData, reorderByReportStatus]);

    return (
        <div>
            <div className="page-breadcrumb d-md-flex align-items-center mb-3">
                <div className="breadcrumb-title pr-3">Question Report System</div>
                <div className="pl-3">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-0 p-0">
                            <li className="breadcrumb-item">
                                <a href="javascript:void(0)">
                                    <i className="bx bx-home-alt"></i>
                                </a>
                            </li>
                        </ol>
                    </nav>
                </div>
                <div className="ml-auto">
                    <div className="btn-group">
                        <button
                            type="button"
                            className="btn btn-primary mr-2"
                            onClick={openFilterModal}
                        >
                            <i className="bx bx-filter-alt mr-1"></i> Filter
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={refreshData}
                            disabled={showLoader}
                        >
                            {showLoader ? 'Loading...' : 'Refresh Data'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter Modal */}
            <Modal
                show={showFilterModal}
                onHide={() => setShowFilterModal(false)}
                backdrop="static"
                keyboard={false}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Filter Options</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="container">
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label htmlFor="status-filter" className="form-label">Status</label>
                                <select
                                    id="status-filter"
                                    className="form-control"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value={REPORT_STATUS.ALL}>All</option>
                                    <option value={REPORT_STATUS.RESOLVED}>Resolved</option>
                                    <option value={REPORT_STATUS.UNRESOLVED}>Unresolved</option>
                                </select>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Start Date</label>
                                <div>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={startDate ? startDate.toISOString().split('T')[0] : ''}
                                        onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">End Date</label>
                                <div>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={endDate ? endDate.toISOString().split('T')[0] : ''}
                                        onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                                        min={startDate ? startDate.toISOString().split('T')[0] : ''}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label htmlFor="subject-filter" className="form-label">Subject</label>
                                <select
                                    id="subject-filter"
                                    className="form-control"
                                    value={selectedSubject}
                                    onChange={(e) => setSelectedSubject(e.target.value)}
                                >
                                    <option value="">All Subjects</option>
                                    {subjects.map(subject => (
                                        <option key={subject.id} value={subject.id}>{subject.title}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="category-filter" className="form-label">Category</label>
                                <select
                                    id="category-filter"
                                    className="form-control"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    <option value="">All Categories</option>
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>{category.title}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
                        Close
                    </Button>
                    <Button variant="outline-secondary" onClick={clearFilters}>
                        Clear Filters
                    </Button>
                    <Button variant="primary" onClick={applyFilters}>
                        Apply Filters
                    </Button>
                </Modal.Footer>
            </Modal>

            {showLoader ? (
                <Shimmer width={"100%"} height={40} />
            ) : (
                <div className="card">
                    <div className="card-body">
                        <div style={{ padding: '10px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <div>
                                <label htmlFor="item-count-select" style={{ marginRight: '10px', fontWeight: 'bold' }}>
                                    Items per page:
                                </label>
                                <select
                                    id="item-count-select"
                                    onChange={handleItemCountChange}
                                    value={itemCount}
                                >
                                    {ITEM_COUNT_OPTIONS.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table
                                className="table table-striped table-bordered mb-0"
                                id="table1"
                            >
                                <thead className="thead-dark">
                                    <tr>
                                        <th align="center">S.No.</th>
                                        <th align="center">Created At</th>
                                        <th align="center">Q.No.</th>
                                        <th align="center">Subject</th>
                                        <th align="center">Category</th>
                                        <th align="center">Shift</th>
                                        <th align="center">Status</th>
                                        <th align="center">Problem</th>

                                        <th align="center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {showShimmer ? (
                                        <tr>
                                            <td colSpan="10">
                                                <Shimmer width={"100%"} height={40} />
                                            </td>
                                        </tr>
                                    ) : questionReportFilterData && questionReportFilterData.length > 0 ? (
                                        questionReportFilterData.map((row, i) => (
                                            <QuestionReportSystemRow
                                                key={row.id || i}
                                                row={row}
                                                index={i}
                                                onStatusUpdate={handleRowUpdate}
                                                currentPage={currentPage}
                                                itemCount={itemCount}
                                            />
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="10" className="text-center">
                                                No reported questions found for the selected filter.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            <br />
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    {offset > 0 && !showLoader && (
                                        <button
                                            aria-label="previous"
                                            onClick={() => setOffset(offset - 1)}
                                            className="btn btn-dark mr-1"
                                        >
                                            Previous
                                        </button>
                                    )}

                                    {!isLast && !showLoader && (
                                        <button
                                            aria-label="next"
                                            onClick={() => setOffset(offset + 1)}
                                            className="btn btn-primary mr-1"
                                        >
                                            Next
                                        </button>
                                    )}
                                </div>
                                <div>
                                    <span className="mr-2">Page {currentPage + 1} of {totalPages}</span>
                                    <span>Total Items: {totalItems}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionReportSystem;
